/**
 * Thin WebSocket wrapper for the Antioch workspace server's agent session API.
 *
 * One transport = one session. The webapp opens the WS with
 * ``cwd=<project>&resume=<session_id>`` query params; the server
 * spawns a ``ClaudeSDKClient`` and bridges frames verbatim.
 *
 * The transport is framework-agnostic on purpose — tests drive it
 * with a standard ``WebSocket`` mock; the provider in
 * ``components/AgentPanel/AgentPanelProvider.tsx`` wraps it in React
 * state.
 *
 * Auth contract
 * -------------
 * The pod's Envoy front door (``packages/rome/helm-charts/antioch-
 * workspace/templates/configmap-envoy.yaml``) gates ``/workspace/*`` on
 * the workspace JWT. Envoy accepts the JWT as ``Authorization: Bearer``,
 * ``?token=`` query param, OR an ``antioch_auth`` cookie.
 *
 * The webapp lives at the platform origin (``app.antioch.com``); the
 * workspace server lives behind Envoy at the per-workspace hostname
 * (``ws-XXXX.<env>.antioch.com``). To reach the right host the caller
 * MUST supply ``baseUrl`` set to the workspace's HTTPS origin (read
 * from ``streamTarget.httpBaseUrl`` / ``connectedWorkspace.hostname``).
 * Without it we'd hit ``app.antioch.com/workspace/*`` and 404.
 *
 * Auth is attached as ``?token=<workspaceToken>`` on the WS URL —
 * browsers don't let JS set ``Authorization`` headers on WebSocket
 * handshakes, so the query-param flavor of Envoy's JWT extractor is
 * the only reliable cross-origin path. The token comes from Rome's
 * ``serviceTokens.workspace_token`` (issued for the audience the pod's
 * Envoy validates against).
 */

import * as Sentry from '@sentry/react';
import type { AgentSpeed, InboundFrame, OutboundFrame, PermissionMode } from './types';

const WORKSPACE_AGENT_PATH_PREFIX = '/workspace/agent';

export interface AgentTransportOptions {
  /** Absolute project cwd for the session. */
  cwd: string;
  /** Existing session id to resume, or undefined for a fresh one. */
  resumeSessionId?: string;
  /**
   * Reasoning effort override for this session ("low" | "medium" |
   * "high" | "xhigh" | "max"). Omit to let the workspace server fall
   * back to whatever ``settings.json`` resolves to.
   */
  effort?: string;
  /**
   * Claude Code speed mode. ``fast`` enables the CLI's fast mode via
   * the workspace server; ``normal`` explicitly leaves it off.
   */
  speed?: AgentSpeed | string;
  /** Claude Code permission mode for SDK startup. */
  permissionMode?: PermissionMode;
  /**
   * Last seq the client observed for this session, sent as
   * ``?since_seq=N`` on the URL. The server replays frames with
   * seq > N from its bounded ring; if the gap is too large to
   * replay, it returns the current head_seq on the
   * ``session_attached`` envelope's ``replayed=false`` and the
   * client must full-rehydrate from the history endpoint. Omit
   * (or pass 0) on first attach.
   */
  sinceSeq?: number;
  /** Per-frame callback. Called once per outbound server frame. */
  onFrame: (frame: OutboundFrame) => void;
  /** Called when the socket closes, voluntarily or with an error. */
  onClose?: (event: CloseEvent) => void;
  /** Called on any transport error. */
  onError?: (error: unknown) => void;
  /**
   * HTTPS origin of the workspace's Envoy (``https://ws-XXXX.<env>.antioch.com``).
   * Defaults to ``window.location.origin`` for local dev where the
   * Vite proxy forwards ``/workspace/*`` to the workspace server on
   * ``localhost:8899``. In a deployed webapp the platform origin
   * does NOT proxy to per-workspace pods, so callers MUST pass the
   * workspace's URL (from ``streamTarget.httpBaseUrl``).
   */
  baseUrl?: string;
  /**
   * Workspace JWT (``serviceTokens.workspace_token``). Appended as
   * ``?token=`` to the WS URL so Envoy can authenticate the
   * handshake. Optional — local dev skips the Envoy gate, and tests
   * use a mocked ``WebSocket``.
   */
  workspaceToken?: string | null;
}

export class AgentTransport {
  private ws: WebSocket | null = null;
  private readonly url: string;
  private readonly onFrame: AgentTransportOptions['onFrame'];
  private readonly onClose?: AgentTransportOptions['onClose'];
  private readonly onError?: AgentTransportOptions['onError'];

  constructor(options: AgentTransportOptions) {
    this.url = AgentTransport.buildUrl(options);
    this.onFrame = options.onFrame;
    this.onClose = options.onClose;
    this.onError = options.onError;
  }

  /**
   * Open the socket. Resolves once the WebSocket is ``OPEN``, rejects
   * on error. Subsequent frames arrive via ``onFrame``.
   */
  async connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      const ws = new WebSocket(this.url);
      this.ws = ws;
      let settled = false;

      ws.onopen = () => {
        if (settled) return;
        settled = true;
        resolve();
      };
      ws.onmessage = (ev) => {
        let frame: OutboundFrame;
        try {
          frame = JSON.parse(ev.data as string) as OutboundFrame;
        } catch (err) {
          // Malformed wire data is a genuine transport-level failure —
          // route it to ``onError`` so the session reflects a broken pipe.
          this.onError?.(err);
          return;
        }
        try {
          this.onFrame(frame);
        } catch (err) {
          // A handler/reducer exception is an application bug, NOT a
          // transport drop. Routing it through ``onError`` would flip the
          // session to a fatal "Lost connection to the workspace server"
          // state — and because that's the ``onError`` path, not
          // ``onClose``, it would never reconnect, turning a single
          // frame-handling bug into a permanently dead chat on a socket
          // that is still wide open. Report it and keep the session alive;
          // subsequent frames (and ``?since_seq=`` replay on any later
          // reconnect) reconcile whatever this frame failed to apply.
          Sentry.captureException(err, {
            tags: { feature: 'agent-transport' },
            extra: { frameType: (frame as { type?: string }).type ?? 'unknown' },
          });
        }
      };
      ws.onerror = (err) => {
        if (!settled) {
          settled = true;
          reject(err);
        } else {
          this.onError?.(err);
        }
      };
      ws.onclose = (ev) => {
        this.onClose?.(ev);
        if (!settled) {
          settled = true;
          reject(ev);
        }
      };
    });
  }

  /**
   * Send an inbound (webapp → server) frame.
   *
   * :return: ``true`` if the frame was written to the socket,
   *     ``false`` when the socket is not currently open.
   */
  send(frame: InboundFrame): boolean {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return false;
    }
    this.ws.send(JSON.stringify(frame));
    return true;
  }

  /** Close the socket voluntarily. */
  close(code = 1000): void {
    this.ws?.close(code);
    this.ws = null;
  }

  /** True if the underlying WebSocket is ``OPEN``. */
  get isOpen(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  /**
   * Build the ``/workspace/agent/project/session`` WebSocket URL for the given cwd.
   */
  static buildUrl(options: AgentTransportOptions): string {
    const baseUrl = options.baseUrl ?? AgentTransport.defaultBaseUrl();
    const wsUrl = new URL(`${WORKSPACE_AGENT_PATH_PREFIX}/project/session`, baseUrl);
    // Switch http(s) → ws(s) protocols
    wsUrl.protocol = wsUrl.protocol === 'https:' ? 'wss:' : 'ws:';
    wsUrl.searchParams.set('cwd', options.cwd);
    if (options.resumeSessionId) {
      wsUrl.searchParams.set('resume', options.resumeSessionId);
    }
    if (options.effort) {
      wsUrl.searchParams.set('effort', options.effort);
    }
    if (options.speed) {
      wsUrl.searchParams.set('speed', options.speed);
    }
    if (options.permissionMode) {
      wsUrl.searchParams.set('permission_mode', options.permissionMode);
    }
    // ``since_seq`` lets a reconnecting client ask the server to
    // replay only the frames it missed during the drop. ``0`` is
    // the implicit fresh-attach default; the transcript hydrates
    // from REST instead of the ring.
    if (options.sinceSeq != null && options.sinceSeq > 0) {
      wsUrl.searchParams.set('since_seq', String(options.sinceSeq));
    }
    if (options.workspaceToken) {
      wsUrl.searchParams.set('token', options.workspaceToken);
    }
    return wsUrl.toString();
  }

  private static defaultBaseUrl(): string {
    if (typeof window !== 'undefined' && window.location) {
      return window.location.origin;
    }
    return 'http://localhost';
  }
}
